import { useMemo, useState } from 'react';
import { Select } from '@customerio/pluma-components/react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useAttributeNameSearch, useAttributeValues } from '@/services/db/attribute/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { isEmptyString } from '@/utils/string';
import { peopleFilterBuilder } from './builder';
import { ATTRIBUTE_CONDITION_TYPE } from './attribute-condition';
import { groupsForFamilies } from './attribute-filter';
import { AttributeRow as SharedAttributeRow } from '@/features/dryad';
import { PEOPLE_SEARCH_OPERATOR_FAMILIES } from './attribute-plugin';
import { ConditionTypeSelect } from './condition-type-select';

type AttributeRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
};

type AttributeNameOption = { value: string; label: string };

/**
 * The attribute-name picker's options: the environment's bounded
 * sent-attributes list as the local seed, plus debounced server search.
 * Searched names accumulate so a picked search result keeps its option after
 * the search clears.
 *
 * The merged pool is narrowed here by case-insensitive substring on the typed
 * term. The row controls the select's search input, which turns Pluma's own
 * client-side filter off (`shouldFilter` is false whenever `searchInputValue`
 * or `onSearchInput` is supplied), so filtering is this hook's job. The stored
 * name is appended after filtering, so a saved condition renders its label
 * instead of going blank while a non-matching term is typed.
 *
 * A typed term that no option matches exactly leads the list as its own
 * option, so any attribute name can be specified rather than only picked.
 * Because the term externally controls the select's input, the row clears it
 * on select and on close: a leftover term is invisible in the reopened input
 * yet still seeds that custom option, so later keystrokes would append to it.
 */
function useAttributeNameOptions(selectedField: string) {
  const workspaceId = requireWorkspaceId();
  const environment = useEnvironmentOptional();
  const localAttributes = useMemo(() => environment.data?.environment?.sent_attributes ?? [], [environment.data]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm] = useDebouncedValue(searchTerm, 300);
  const search = useAttributeNameSearch(workspaceId, debouncedTerm);

  const [searchedAttributes, setSearchedAttributes] = useState<readonly string[]>([]);
  const searchMatches = search.data;
  if (searchMatches != null) {
    const merged = [...new Set([...searchedAttributes, ...searchMatches])];
    if (merged.length !== searchedAttributes.length) {
      setSearchedAttributes(merged);
    }
  }

  const options: AttributeNameOption[] = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    const names = new Set(
      [...localAttributes, ...searchedAttributes].filter((name) => name.toLowerCase().includes(needle)),
    );
    const custom = !isEmptyString(searchTerm) && !names.has(searchTerm) ? [searchTerm] : [];
    if (!isEmptyString(selectedField)) {
      names.add(selectedField);
    }
    return [...custom, ...names].map((name) => ({ value: name, label: name }));
  }, [localAttributes, searchedAttributes, selectedField, searchTerm]);

  return { options, searchTerm, setSearchTerm, isSearching: search.isFetching };
}

/**
 * The people surface's attribute row: the shared row, plus the profile-attribute
 * name picker (debounced server search merged with the environment's seed list)
 * that only this surface has.
 */
export function AttributeRow({ id }: AttributeRowProps) {
  const workspaceId = requireWorkspaceId();
  const meta = peopleFilterBuilder.useMeta();
  const { state } = peopleFilterBuilder.useLeafState(id, ATTRIBUTE_CONDITION_TYPE);

  // "Select an attribute" renders only once the picker has been visited
  // (closing its dropdown) — or from the start when the row mounted with a
  // name already chosen — so a freshly added row doesn't open covered in red.
  const [fieldTouched, setFieldTouched] = useState(() => state.field !== '');

  const names = useAttributeNameOptions(state.field);

  const isDisabled = meta.isDisabled || !meta.canViewAttributeValues;

  const values = useAttributeValues(workspaceId, meta.canViewAttributeValues ? state.field : '');
  const valueOptions = useMemo(() => (values.data ?? []).map((value) => ({ value, label: value })), [values.data]);

  return (
    <SharedAttributeRow
      id={id}
      conditionType={ATTRIBUTE_CONDITION_TYPE}
      operatorGroups={(field) => groupsForFamilies(PEOPLE_SEARCH_OPERATOR_FAMILIES, field)}
      typeSelect={<ConditionTypeSelect nodeId={id} currentType={ATTRIBUTE_CONDITION_TYPE} />}
      isDisabled={isDisabled}
      valueOptions={valueOptions}
      isLoadingValueOptions={values.isFetching}
      isFieldTouched={fieldTouched}
      onFieldTouched={() => setFieldTouched(true)}
      renderNamePicker={({ field, error, isDisabled: pickerDisabled, onFieldChange, onTouched }) => (
        <Select
          data-testid="dryad-attribute-name"
          ariaLabel="Profile attribute"
          size="sm"
          placeholder="Profile attribute"
          options={names.options}
          value={field === '' ? undefined : field}
          error={error}
          isDisabled={pickerDisabled}
          isSearchable
          searchPlaceholder="Find or specify an attribute"
          searchInputValue={names.searchTerm}
          onSearchInput={(event) => names.setSearchTerm(event.target.value)}
          isLoading={names.isSearching}
          onOpenChange={(isOpen: boolean) => {
            if (!isOpen) {
              onTouched();
              names.setSearchTerm('');
            }
          }}
          onChange={(value: string | null) => {
            names.setSearchTerm('');
            if (value != null) {
              onFieldChange(value);
            }
          }}
        />
      )}
    />
  );
}
